import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '@/lib/hooks'
import { logoutUser } from '@/features/auth/authSlice'
import { toggleTheme } from '@/features/ui/uiSlice'
import { setCurrentLanguage } from '@/features/lang/langSlice'
import Dropdown from '@/components/ui/Dropdown'
import { Avatar, AvatarFallback } from '@/components/ui/Avatar'
import { 
  Settings, 
  LogOut, 
  Sun, 
  Moon, 
  Globe
} from 'lucide-react'

export default function UserMenu() {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { i18n } = useTranslation()
  
  const { user } = useAppSelector((state) => state.auth)
  const { theme } = useAppSelector((state) => state.ui)
  const { currentLanguage } = useAppSelector((state) => state.lang)

  const handleSettingsClick = () => {
    navigate('/settings')
  }

  const handleLogout = () => {
    dispatch(logoutUser())
  }

  const handleThemeToggle = () => {
    dispatch(toggleTheme())
  }

  const handleLanguageToggle = async () => {
    const newLang = currentLanguage === 'en' ? 'tr' : 'en'
    dispatch(setCurrentLanguage(newLang))
    await i18n.changeLanguage(newLang)
  }

  if (!user) return null

  return (
    <Dropdown>
      {/* 🎯 MINIMAL: Simple trigger */}
      <Dropdown.Trigger className="flex items-center gap-2 p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
        <Avatar className="h-8 w-8">
          <AvatarFallback className="bg-primary-600 text-white text-sm font-medium">
            {user.name?.charAt(0).toUpperCase() || 'U'}
          </AvatarFallback>
        </Avatar>
        
        {/* 🎯 CLEAN: Minimal user info - desktop only */}
        <div className="hidden md:block text-left">
          <p className="text-sm font-medium text-gray-900 dark:text-white truncate max-w-[100px]">
            {user.name}
          </p>
        </div>
      </Dropdown.Trigger>

      <Dropdown.Content align="right" className="w-48">
        {/* 🎯 HEADER: User info */}
        <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {user.name}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {user.email}
          </p>
        </div>

        {/* 🎯 ACTIONS: Essential controls */}
        <Dropdown.Item onClick={handleSettingsClick}>
          <Settings className="h-4 w-4 mr-3" />
          Settings
        </Dropdown.Item>

        <Dropdown.Item onClick={handleThemeToggle}>
          {theme === 'light' ? (
            <Moon className="h-4 w-4 mr-3 flex-shrink-0" />
          ) : (
            <Sun className="h-4 w-4 mr-3 flex-shrink-0" />
          )}
          {theme === 'light' ? 'Dark mode' : 'Light mode'}
        </Dropdown.Item>

        <Dropdown.Item onClick={handleLanguageToggle}>
          <Globe className="h-4 w-4 mr-3" />
          {currentLanguage === 'en' ? 'Türkçe' : 'English'}
        </Dropdown.Item>

        <Dropdown.Separator />

        <Dropdown.Item onClick={handleLogout} className="text-red-600 dark:text-red-400">
          <LogOut className="h-4 w-4 mr-3" />
          Sign out
        </Dropdown.Item>
      </Dropdown.Content>
    </Dropdown>
  )
} 